import { useApolloClient } from "@apollo/client";
import { ImagingAssetKind } from "elephant-web/gql";
import { useCallback, useEffect, useRef, useState } from "react";
import { fetchImagingAssetUrl } from "../hooks/imaging-asset-url";
import {
  fetchBlobWithProgress,
  readCachedBlob,
  writeSessionBlob,
} from "./cine-asset-store";
import type { CineAvailability } from "./cine-metadata";

const TRANSCODE_POLL_MS = 5000;

export type CineSourceState =
  | "LOADING"
  | "TRANSCODING"
  | "READY"
  | "FAILED"
  | "UNAVAILABLE";

export type CineSource = {
  state: CineSourceState;
  src: string | null;
  progress: number | null;
  retry: () => void;
  reportPlaybackError: () => void;
};

export const useCineSource = ({
  imagingInstanceId,
  availability,
  sizeBytes,
  enabled,
}: {
  imagingInstanceId: string;
  availability: CineAvailability | null;
  sizeBytes: number | null;
  enabled: boolean;
}): CineSource => {
  const apolloClient = useApolloClient();
  const reloadRef = useRef<(() => void) | null>(null);
  const playbackErrorRef = useRef<(() => void) | null>(null);
  const [state, setState] = useState<CineSourceState>("LOADING");
  const [src, setSrc] = useState<string | null>(null);
  const [progress, setProgress] = useState<number | null>(null);

  const retry = useCallback(() => reloadRef.current?.(), []);
  const reportPlaybackError = useCallback(
    () => playbackErrorRef.current?.(),
    [],
  );

  useEffect(() => {
    if (!enabled || !availability) return;
    if (availability === "UNAVAILABLE") {
      setState("UNAVAILABLE");
      return;
    }

    const controller = new AbortController();
    let createdObjectUrl: string | null = null;
    let pollTimer: ReturnType<typeof setTimeout> | undefined;
    let hasRecovered = false;

    const publishBlob = (blob: Blob) => {
      createdObjectUrl = URL.createObjectURL(blob);
      setSrc(createdObjectUrl);
      setProgress(1);
      setState("READY");
    };

    const streamDirectly = (signedUrl: string) => {
      setSrc(signedUrl);
      setProgress(null);
      setState("READY");
    };

    const load = async ({ resign }: { resign: boolean }): Promise<void> => {
      setState("LOADING");
      setProgress(null);
      let signedUrl: string | null = null;
      try {
        const cached = await readCachedBlob(
          imagingInstanceId,
          ImagingAssetKind.Cine,
        );
        if (controller.signal.aborted) return;
        if (cached) {
          publishBlob(cached);
          return;
        }

        signedUrl = await fetchImagingAssetUrl(
          apolloClient,
          imagingInstanceId,
          ImagingAssetKind.Cine,
          { bypassCache: resign },
        );
        if (controller.signal.aborted) return;
        if (!signedUrl) {
          if (availability === "FAILED") {
            setState("FAILED");
            return;
          }
          setState("TRANSCODING");
          pollTimer = setTimeout(
            () => void load({ resign: false }),
            TRANSCODE_POLL_MS,
          );
          return;
        }

        const blob = await fetchBlobWithProgress(signedUrl, {
          expectedBytes: sizeBytes,
          signal: controller.signal,
          onProgress: setProgress,
        });
        if (controller.signal.aborted) return;
        writeSessionBlob(imagingInstanceId, ImagingAssetKind.Cine, blob);
        publishBlob(blob);
      } catch {
        if (controller.signal.aborted) return;
        // Reading the bytes cross-origin needs a CORS grant on the artifact
        // bucket; handing the url to the element does not. Fall back so a
        // missing grant costs determinate progress and offline caching rather
        // than playback itself.
        if (signedUrl) streamDirectly(signedUrl);
        else setState("FAILED");
      }
    };

    reloadRef.current = () => {
      if (pollTimer) clearTimeout(pollTimer);
      hasRecovered = false;
      void load({ resign: true });
    };

    // A presigned url can outlive its one-hour server TTL mid-consultation, and
    // playback failing is the first evidence of it — so re-sign once here.
    playbackErrorRef.current = () => {
      if (hasRecovered) {
        setState("FAILED");
        return;
      }
      hasRecovered = true;
      void load({ resign: true });
    };

    void load({ resign: false });

    return () => {
      controller.abort();
      reloadRef.current = null;
      playbackErrorRef.current = null;
      if (pollTimer) clearTimeout(pollTimer);
      if (createdObjectUrl) URL.revokeObjectURL(createdObjectUrl);
      setSrc(null);
    };
  }, [apolloClient, availability, enabled, imagingInstanceId, sizeBytes]);

  return { state, src, progress, retry, reportPlaybackError };
};
